import { Directive, ElementRef, Renderer2, AfterViewInit, OnDestroy, NgZone } from "@angular/core";

@Directive({
  standalone: false,
  selector: "ags-header",
})
export class A11yFixDirective implements AfterViewInit, OnDestroy {
  private observer: MutationObserver | null = null;
  private debounceTimer: any = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private ngZone: NgZone
  ) { }

  ngAfterViewInit() {
    this.ngZone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => {
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(() => this.fixAccessibility(), 50);
      });

      this.observer.observe(this.el.nativeElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["role", "aria-label"]
      });
    });

    setTimeout(() => this.fixAccessibility(), 100);
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  private fixAccessibility() {
    const navbar = this.el.nativeElement.querySelector("nav.navbar");
    if (navbar) {
      this.renderer.setAttribute(navbar, "aria-label", "Main navigation");
    }

    const navbarNavs = this.el.nativeElement.querySelectorAll(".navbar-nav[role='list']");
    navbarNavs.forEach((nav: Element) => {
      this.renderer.setAttribute(nav, "role", "presentation");
    });

    const navItems = this.el.nativeElement.querySelectorAll(".navbar-nav[role='presentation'] > li.nav-item");
    navItems.forEach((item: Element) => {
      this.renderer.setAttribute(item, "role", "none");
    });

    const rightMenu = this.el.nativeElement.querySelector("div.right-menu");
    if (rightMenu) {
      this.renderer.removeAttribute(rightMenu, "role");
    }
  }
}

@Directive({
  standalone: false,
  selector: "ags-side-navigation",
})
export class AgsSideNavA11yFixDirective implements AfterViewInit, OnDestroy {
  private observer: MutationObserver | null = null;
  private debounceTimer: any = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private ngZone: NgZone
  ) { }

  ngAfterViewInit() {
    this.ngZone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => {
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(() => this.fixAccessibility(), 50);
      });

      this.observer.observe(this.el.nativeElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["aria-selected"]
      });
    });

    setTimeout(() => this.fixAccessibility(), 100);
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  private fixAccessibility() {
    const items = this.el.nativeElement.querySelectorAll("[aria-selected]");
    items.forEach((el: Element) => {
      this.renderer.removeAttribute(el, "aria-selected");
    });
  }
}

@Directive({
  standalone: false,
  selector: "ag-grid-angular",
})
export class AgGridA11yFixDirective implements AfterViewInit, OnDestroy {
  private observer: MutationObserver | null = null;
  private debounceTimer: any = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private ngZone: NgZone
  ) { }

  ngAfterViewInit() {
    this.ngZone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => {
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(() => this.fixAccessibility(), 50);
      });

      this.observer.observe(this.el.nativeElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["role", "aria-rowindex", "aria-selected", "aria-colindex", "aria-sort", "aria-expanded", "aria-rowcount", "aria-colcount", "aria-multiselectable", "aria-describedby"]
      });
    });

    setTimeout(() => this.fixAccessibility(), 100);
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  private fixAccessibility() {
    this.renderer.removeAttribute(this.el.nativeElement, "role");

    const agRoot = this.el.nativeElement.querySelector(".ag-root");
    if (agRoot) {
      this.renderer.removeAttribute(agRoot, "role");
      this.renderer.removeAttribute(agRoot, "aria-rowcount");
      this.renderer.removeAttribute(agRoot, "aria-colcount");
      this.renderer.removeAttribute(agRoot, "aria-multiselectable");
    }

    const rowgroups = this.el.nativeElement.querySelectorAll("[role='rowgroup']");
    rowgroups.forEach((el: Element) => {
      this.renderer.removeAttribute(el, "role");
    });

    const rows = this.el.nativeElement.querySelectorAll("[role='row']");
    rows.forEach((el: Element) => {
      this.renderer.removeAttribute(el, "role");
      this.renderer.removeAttribute(el, "aria-rowindex");
      this.renderer.removeAttribute(el, "aria-selected");
      this.renderer.removeAttribute(el, "aria-expanded");
    });

    const gridcells = this.el.nativeElement.querySelectorAll("[role='gridcell']");
    gridcells.forEach((el: Element) => {
      this.renderer.removeAttribute(el, "role");
      this.renderer.removeAttribute(el, "aria-colindex");
    });

    const columnheaders = this.el.nativeElement.querySelectorAll("[role='columnheader']");
    columnheaders.forEach((el: Element) => {
      this.renderer.removeAttribute(el, "role");
      this.renderer.removeAttribute(el, "aria-colindex");
      this.renderer.removeAttribute(el, "aria-sort");
    });

    const cells = this.el.nativeElement.querySelectorAll(".ag-cell[aria-colindex]");
    cells.forEach((el: Element) => {
      this.renderer.removeAttribute(el, "aria-colindex");
      this.renderer.removeAttribute(el, "aria-describedby");
    });

    const headerCells = this.el.nativeElement.querySelectorAll(".ag-header-cell[aria-colindex]");
    headerCells.forEach((el: Element) => {
      this.renderer.removeAttribute(el, "aria-colindex");
      this.renderer.removeAttribute(el, "aria-sort");
    });

    const fullWidthRows = this.el.nativeElement.querySelectorAll("div.ag-full-width-container > div[comp-id]");
    fullWidthRows.forEach((el: Element) => {
      this.renderer.removeAttribute(el, "role");
      this.renderer.removeAttribute(el, "aria-rowindex");
    });

    const headerRows = this.el.nativeElement.querySelectorAll("div.ag-header-container > .ag-header-row");
    headerRows.forEach((el: Element) => {
      this.renderer.removeAttribute(el, "role");
      this.renderer.removeAttribute(el, "aria-rowindex");
    });

    const headerContainer = this.el.nativeElement.querySelectorAll("div.ag-header-container");
    headerContainer.forEach((el: Element) => {
      this.renderer.removeAttribute(el, "role");
    });

    const expandedCells = this.el.nativeElement.querySelectorAll("div.ag-center-cols-container > div[comp-id][aria-expanded]");
    expandedCells.forEach((el: Element) => {
      this.renderer.removeAttribute(el, "aria-expanded");
    });

    const inputs = this.el.nativeElement.querySelectorAll("input[autocomplete]");
    inputs.forEach((el: Element) => {
      this.renderer.removeAttribute(el, "autocomplete");
    });

    const scrollableRegions = this.el.nativeElement.querySelectorAll(".ag-body-viewport, .ag-center-cols-viewport");
    scrollableRegions.forEach((el: Element) => {
      this.renderer.setAttribute(el, "tabindex", "0");
    });
  }
}

@Directive({
  standalone: false,
  selector: "ng-select",
})
export class NgSelectA11yFixDirective implements AfterViewInit, OnDestroy {
  private observer: MutationObserver | null = null;
  private debounceTimer: any = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private ngZone: NgZone
  ) { }

  ngAfterViewInit() {
    this.ngZone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => {
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(() => this.fixAccessibility(), 50);
      });

      this.observer.observe(this.el.nativeElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["autocomplete"]
      });
    });

    setTimeout(() => this.fixAccessibility(), 100);
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  private fixAccessibility() {
    const inputs = this.el.nativeElement.querySelectorAll("input[autocomplete]");
    inputs.forEach((el: Element) => {
      this.renderer.removeAttribute(el, "autocomplete");
    });
  }
}
